import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { BillingTab, DebtorsTab, PrescriptionsTab, SettlementTab } from '../components/rent.js';

/**
 * `/sprava-bytu` — Sprava najmu (krok FAK3). Polozka menu existovala uz
 * z kroku O1 jako PRIPRAVUJE SE; tenhle krok ji naplnuje obsahem, stejne
 * jako FAK1 udelal s `/fakturace`.
 *
 * Ctyri zalozky = ctyri body zadani: predpisy najemneho, hromadne mesicni
 * vystaveni, uhrady a dluznici, rocni vyuctovani sluzeb (z. 67/2013).
 */

type Tab = 'prescriptions' | 'billing' | 'debtors' | 'settlement';

const LOCALE = 'cs' as const;

const TAB_LABEL_KEY: Readonly<Record<Tab, MessageKey>> = {
  prescriptions: 'webAdmin.spravaBytu.tabPrescriptions',
  billing: 'webAdmin.spravaBytu.tabBilling',
  debtors: 'webAdmin.spravaBytu.tabDebtors',
  settlement: 'webAdmin.spravaBytu.tabSettlement',
};

export function SpravaBytuPage(): React.JSX.Element {
  const [tab, setTab] = React.useState<Tab>('prescriptions');

  return (
    <div className="p-6" data-testid="sprava-bytu-page">
      <h1 className="mb-1 text-xl font-bold">
        {t('webAdmin.spravaBytu.title', { locale: LOCALE })}
      </h1>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        {t('webAdmin.spravaBytu.perex', { locale: LOCALE })}
      </p>

      <div
        role="tablist"
        aria-label={t('webAdmin.spravaBytu.title', { locale: LOCALE })}
        /* Krok FUNK1 bod 2: na 390 px se ctyri zalozky do radky nevejdou
           a popisek se urizl („Vyúčt…"). `flex-wrap` je zlomi do dvou radku. */
        className="mb-4 flex flex-wrap gap-2"
      >
        {(['prescriptions', 'billing', 'debtors', 'settlement'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className="rh-btn"
            onClick={() => setTab(key)}
            data-testid={`sprava-bytu-tab-${key}`}
          >
            {t(TAB_LABEL_KEY[key], { locale: LOCALE })}
          </button>
        ))}
      </div>

      {tab === 'prescriptions' && <PrescriptionsTab />}
      {tab === 'billing' && <BillingTab />}
      {tab === 'debtors' && <DebtorsTab />}
      {tab === 'settlement' && <SettlementTab />}
    </div>
  );
}
